import { useGetRBE } from '@wwe-portal/web/providers/queries/rbe';
import { PlayerPoints, PlayerRank } from '@wwe-portal/entities/rbe.type';
import { useMemo } from 'react';

const removeDuplicate = <T extends { userId: string }>(array: T[]) => {
  const set = new Set();
  const result: T[] = [];
  for (const item of array) {
    if (set.has(item.userId)) {
      continue;
    }
    set.add(item.userId);
    result.push(item);
  }
  return result;
};

const correctPlayerRank = (
  player: Partial<PlayerRank> | undefined,
  previousPlayer?: Partial<PlayerRank>
) => {
  if (!player) return undefined;
  if (!previousPlayer) return Number(player?.playerRank);
  if (player.playerPoints === previousPlayer.playerPoints) {
    return Number(previousPlayer.playerRank);
  }
  return Number(player.playerRank);
};

export const correctLeaderboardOrder = <
  T extends {
    abovePlayer: PlayerRank | undefined;
    currentPlayer: PlayerRank | undefined;
    rows?: PlayerPoints[] | undefined;
  },
>(
  data?: T,
  rowsLimit: number = 10
) => {
  if (data == null) return undefined;
  const clonedData = [...(data.rows ?? [])];
  if (data.currentPlayer?.userId) {
    clonedData.push(data.currentPlayer);
  }
  if (data.abovePlayer?.userId) {
    clonedData.push(data.abovePlayer);
  }
  const sortedData = removeDuplicate(clonedData).sort((player1, player2) => {
    return (player2?.playerPoints ?? 0) - (player1?.playerPoints ?? 0);
  });

  const topPlayers: Partial<PlayerRank>[] = sortedData.slice(0, rowsLimit);
  for (let index = 0; index < topPlayers.length; index++) {
    const previousItem = topPlayers[index - 1];

    const currentItem = {
      ...topPlayers[index],
      playerRank: index + 1,
    };

    topPlayers[index] = {
      ...currentItem,
      playerRank: correctPlayerRank(currentItem, previousItem),
    };
  }

  const abovePlayer: Partial<PlayerRank> = {
    ...data.abovePlayer,
    playerRank: correctPlayerRank(
      data.abovePlayer,
      topPlayers[topPlayers.length - 1]
    ),
  };

  const currentPlayer: Partial<PlayerRank> = {
    ...data.currentPlayer,
    playerRank: correctPlayerRank(data.currentPlayer, abovePlayer),
  };

  return {
    ...data,
    rows: topPlayers,
    abovePlayer,
    currentPlayer,
  };
};

const sortEvents = <
  T extends {
    endDate: string;
  },
>(
  events: T[] | undefined
) => {
  if (events == null) return events;
  const endedEvents = events.filter(
    (event) => new Date(event.endDate) <= new Date()
  );
  const activeEvents = events
    .filter((event) => new Date(event.endDate) > new Date())
    .sort((a, b) => Date.parse(a.endDate) - Date.parse(b.endDate));

  const result: T[] = [...activeEvents, ...endedEvents];
  return result;
};

export const useLimitedEventV2 = () => {
  const { data, isLoading } = useGetRBE();

  const originSoloEvents = data?.soloEvents;
  const originGlobalEvents = data?.globalEvents;

  const soloEvents = useMemo(() => {
    return sortEvents(originSoloEvents);
  }, [originSoloEvents]);

  const globalEvents = useMemo(() => {
    return sortEvents(originGlobalEvents);
  }, [originGlobalEvents]);

  return {
    globalOrder: data?.globalEventPriority,
    soloEvents,
    globalEvents,
    isLoading,
  };
};
